import type { Rng } from '@/core/Rng';
import type { TrackSample } from '@/world/Track';
import type { TrackTheme } from '@/world/Theme';
import type { City, LandmarkKind } from '@/config/cities';

/**
 * scenery/types.ts — the instance records each zone builder pushes into, and
 * the per-step context handed to zone builders. Everything ends up in a small
 * number of InstancedMesh draw calls (see scenery/builders.ts).
 */

export interface BoxInst {
  x: number;
  y: number; // ground height at the base
  z: number;
  rotY: number;
  w: number;
  h: number;
  d: number;
  color: number;
}
export interface FlatInst {
  x: number;
  y: number;
  z: number;
  rotY: number;
  w: number;
  d: number;
  color: number;
  /** WATER metadata (GS1 rebuild) — course-following water emitters stamp the
   *  emission arc/side/shoreline so the merge post-pass (scenery/water.ts) can
   *  weld their per-step slab train into ONE shoreline-following mesh. Small
   *  free-standing ponds (NYC park) omit them and become organic disc ponds.
   *  Purely additive: buildFlat and every non-water consumer ignore these. */
  arc?: number;
  side?: -1 | 1;
  /** Inner shoreline lateral (units from the road centreline). */
  shore?: number;
}
export interface NeonInst {
  x: number;
  y: number; // absolute centre height
  z: number;
  rotY: number;
  w: number;
  h: number;
  color: number;
}
export interface TreeInst {
  x: number;
  y: number;
  z: number;
  s: number;
  color: number;
}
export interface CarInst {
  x: number;
  y: number;
  z: number;
  rotY: number;
  color: number;
}
/** A simple oriented prop placed on the curb (buses, phone boxes, stalls…). */
export interface PlaceInst {
  x: number;
  y: number;
  z: number;
  rotY: number;
  /** Optional per-instance scale (1 = natural size). */
  s?: number;
  /** Optional per-instance colour for builders that support it. */
  color?: number;
  /** Bus route board / destination blind (mumbai-nits): city-true signage —
   *  Mumbai's BEST double-deckers read "1 · COLABA"; unset keeps the London
   *  default ("23 · WESTMINSTER"). Read only by buildBuses. */
  sign?: { route: string; dest: string };
}
export interface HillInst {
  x: number;
  y: number;
  z: number;
  r: number;
  h: number;
  color: number;
}
/** A single person in a crowd (M36). Tinted per part: torso/shirt, head/skin,
 *  legs/pants. `sit` swaps a standing silhouette for a seated one (beaches,
 *  sea walls, park lawns). `y` is the ground height they stand/sit on. */
export interface PersonInst {
  x: number;
  y: number;
  z: number;
  rotY: number;
  s: number;
  shirt: number;
  skin: number;
  pants: number;
  sit: boolean;
  /** FINISH FANFARE: a CELEBRATING spectator — standing, BOTH arms up, some
   *  holding a scarf overhead, on a hash-derived bounce so the line of heads
   *  is ragged. Optional so every existing crowd (markets, beaches, park
   *  lawns, the grandstand tiers) is untouched: only the finish plaza's rail
   *  crowd sets it. Ignored when `sit` is true. */
  cheer?: boolean;
}
export interface AdScreenInst {
  x: number;
  y: number;
  z: number;
  rotY: number;
  w: number;
  h: number;
  tex: number;
}

/** Every instance list the zones can feed. A city only pays for what it fills. */
export interface SceneryArrays {
  buildings: BoxInst[]; // lit-window boxes
  plain: BoxInst[]; // unlit boxes (trim, stoops, cornices, dark towers…)
  neon: NeonInst[];
  trees: TreeInst[]; // round-blob foliage
  palms: PlaceInst[]; // curved-trunk palm trees
  pines: PlaceInst[]; // tall cone trees (umbrella pines / conifers)
  baobabs: PlaceInst[]; // bottle-trunk flat-crown giants
  grass: FlatInst[];
  ponds: FlatInst[]; // water
  sand: FlatInst[]; // beaches / dunes floor
  cabs: CarInst[];
  buses: PlaceInst[]; // double-deckers (colour per instance)
  phoneBoxes: PlaceInst[];
  scooters: PlaceInst[]; // Vespas, okadas
  boats: PlaceInst[]; // pirogues / fishing boats (on water)
  stalls: PlaceInst[]; // market stalls with bright awnings
  umbrellas: PlaceInst[]; // beach / market parasols
  stiltHouses: PlaceInst[]; // lagoon stilt village
  lanterns: PlaceInst[]; // glowing red lanterns (Tokyo temple town)
  vertSigns: AdScreenInst[]; // vertical glowing sign panels (Tokyo)
  laundry: PlaceInst[]; // laundry lines strung overhead (Napoli)
  fences: PlaceInst[]; // ranch fence segments (Patagonia)
  roofs: BoxInst[]; // pyramid roofs dropped onto buildings (cabins, temples)
  promenade: FlatInst[]; // patterned pavement band (Copacabana waves)
  icebergs: PlaceInst[]; // floating ice chunks (on water)
  saltMounds: PlaceInst[]; // white cones on the pink lake shore
  hills: HillInst[];
  adBuildings: BoxInst[];
  adScreens: AdScreenInst[];
  people: PersonInst[]; // crowds (beaches, parks, plazas, markets, promenades)
}

export function emptyArrays(): SceneryArrays {
  return {
    buildings: [],
    plain: [],
    neon: [],
    trees: [],
    palms: [],
    pines: [],
    baobabs: [],
    grass: [],
    ponds: [],
    sand: [],
    cabs: [],
    buses: [],
    phoneBoxes: [],
    scooters: [],
    boats: [],
    stalls: [],
    umbrellas: [],
    stiltHouses: [],
    lanterns: [],
    vertSigns: [],
    laundry: [],
    fences: [],
    roofs: [],
    promenade: [],
    icebergs: [],
    saltMounds: [],
    hills: [],
    adBuildings: [],
    adScreens: [],
    people: [],
  };
}

/** Everything a zone builder gets for one (distance-step, side) placement slot. */
export interface ZoneCtx {
  rng: Rng;
  /** A SEPARATE seeded stream used ONLY for crowds (scatterPeople), so adding
   *  people never disturbs the main `rng` sequence — every existing building,
   *  tree and prop stays exactly where it was. */
  peopleRng: Rng;
  /** Track sample at this step (position / direction / right vector). */
  s: TrackSample;
  side: -1 | 1;
  /** Progress to the finish, 0..1. */
  progress: number;
  /** Road half-width. */
  hw: number;
  /** Distance between placement steps along the track. */
  step: number;
  theme: TrackTheme;
  city: City;
  /** World position `lateral` units out from the road edge on this side. */
  at: (lateral: number) => [number, number, number];
  a: SceneryArrays;
  /** Progress (0..1) of selected landmarks, for clearings (plazas, water gaps). */
  marks: Partial<Record<LandmarkKind, number>>;
}

export type ZoneBuilder = (ctx: ZoneCtx) => void;
